import { useEffect, useState } from "react";
import IFolder from "../../models/IFolder";
import newFolder from "./NewFolderObject";
import INewFolderProps from "./INewFolderProps";
import folderService from "../../services/folderService";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, MessageBar, Spinner } from "@fluentui/react-components";
import NewFolderCard from "../newFolderCard/NewFolderCard";
import FolderForm from "../form/FolderForm";
import { useTranslation } from "react-i18next";
import Status from "../../enums/Status";
import fileService from "../../services/fileService";
import IBlob from "../../models/IBlob";
import { v4 as uuid } from "uuid";
import { fileToBase64 } from "../../utilities/files";
import useStyleUtils from "../../utilities/styles";
import Ownership from "../../enums/Ownership";
import OwnershipMessage from "../ownershipMessage/OwnershipMessage";

export default function NewFolder(props: INewFolderProps) {
    const { t } = useTranslation();

    const style = useStyleUtils();
    
    const handleChange = (key: string, value?: string) => setForm({ ...form, [key]: value });

    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [form, setForm] = useState<IFolder>(newFolder(props.isPersonal, props.parentFolderId!));
    const [image, setImage] = useState<File>(null!);
    
    const handleImageChange = (img: File) => setImage(img);

    const handleSubmit = () => {
        setStatus(Status.SAVING);

        submit(form, image)
            .then((result: boolean) => {
                if (!result) {
                    setStatus(Status.SAVE_ERROR);
                    return;
                }

                setStatus(Status.SAVE_SUCCESS);
                props.onSave();
                setForm(newFolder(props.isPersonal, props.parentFolderId!));
                setImage(null!);
                setOpen(false);
            })
            .catch(error => console.error(error));
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        setForm(newFolder(props.isPersonal, props.parentFolderId!));
        setImage(null!);
    }, [open, props.isPersonal, props.parentFolderId]);

    const isDisabled = [Status.LOADING, Status.LOAD_ERROR, Status.SAVING, Status.SAVE_SUCCESS].includes(status);

    return (
        <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <NewFolderCard />
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{t("folders.titles.create")}</DialogTitle>
                    <DialogContent>
                        <OwnershipMessage ownership={props.isPersonal ? Ownership.PERSONAL : Ownership.SHARED} isNew={true} />
                        <FolderForm data={form} image={image} onImageChange={handleImageChange} onChange={handleChange} />
                        {
                            status === Status.SAVE_ERROR &&
                            <MessageBar intent="error">{t("folders.errors.saveFolder")}</MessageBar>
                        }
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" disabled={isDisabled}>
                                {t("general.cancel")}
                            </Button>
                        </DialogTrigger>
                        <Button appearance="primary" onClick={handleSubmit} disabled={isDisabled}>
                            {
                                status === Status.SAVING && <Spinner size="extra-small" className={style.mr3} />
                            }
                            
                            {t("general.save")}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

async function submit(folder: IFolder, image?: File) : Promise<boolean> {
    try {
        if (image) {
            const fileExtension = image.name.split('.').pop();
            const filename = `${uuid()}.${fileExtension}`;

            const blob: IBlob = {
                blobName: filename,
                containerName: 'logos'
            };

            const imgUrlResult = await fileService.getUploadUrl(blob);
            if (!imgUrlResult) {
                return false;
            }

            const imgBase64 = await fileToBase64(image, true);
            const uploadResult = await fileService.uploadFile(imgUrlResult.url!, imgBase64);
            if (!uploadResult) {
                return false;
            }

            folder.logoUri = imgUrlResult.url!.split('?')[0];
        } else {
            folder.logoUri = null!;
        }
        
        const result = await folderService.createFolder(folder);
        if (result) {
            return true;
        } else {
            return false;
        }
    } catch (e) {
        console.error(e);
        return false;
    }
}